import { createContext, useCallback, useContext, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import type { EpisodePeopleResponse, PeopleGroup, PersonSummary, Relation } from '../types';

/** Relation choices, with the words families actually use. */
export const RELATION_OPTIONS: Array<{ value: Relation; label: string }> = [
  { value: 'ME', label: 'That’s me' },
  { value: 'MOTHER', label: 'Mother (Ammi)' },
  { value: 'FATHER', label: 'Father (Abbu)' },
  { value: 'BROTHER', label: 'Brother (Bhai)' },
  { value: 'SISTER', label: 'Sister (Baji / Behan)' },
  { value: 'HUSBAND', label: 'Husband' },
  { value: 'WIFE', label: 'Wife' },
  { value: 'SON', label: 'Son' },
  { value: 'DAUGHTER', label: 'Daughter' },
  { value: 'GRANDMOTHER', label: 'Grandmother (Dadi / Nani)' },
  { value: 'GRANDFATHER', label: 'Grandfather (Dada / Nana)' },
  { value: 'AUNT', label: 'Aunt (Khala / Phuppo / Chachi / Mumani)' },
  { value: 'UNCLE', label: 'Uncle (Chacha / Mamu / Khalu / Phuppa)' },
  { value: 'COUSIN', label: 'Cousin' },
  { value: 'NIECE', label: 'Niece' },
  { value: 'NEPHEW', label: 'Nephew' },
  { value: 'IN_LAW', label: 'In-law' },
  { value: 'FRIEND', label: 'Friend' },
  { value: 'OTHER', label: 'Someone else' },
];
const relationLabel = (r: Relation | null) =>
  r ? (r === 'ME' ? 'You' : RELATION_OPTIONS.find((o) => o.value === r)?.label.replace(/ \(.*\)$/, '')) : null;

/** Unnamed groups shown before "Show more" (busy episodes have many one-off faces). */
const FIRST_UNNAMED = 4;

const photos = (n: number) => `${n} ${n === 1 ? 'photo' : 'photos'}`;

/** Whose library (set for a shared episode) and whether the user may only look. */
const PanelContext = createContext<{ owner: string | null; readOnly: boolean }>({ owner: null, readOnly: false });

function FaceImg({ faceId, size = 'lg' }: { faceId: string; size?: 'lg' | 'sm' }) {
  const { owner } = useContext(PanelContext);
  return (
    <img
      src={api.faceImageUrl(faceId, owner)}
      alt=""
      loading="lazy"
      className={`flex-none rounded-full object-cover ring-1 ring-white/15 ${size === 'lg' ? 'h-16 w-16' : 'h-9 w-9'}`}
    />
  );
}

interface Props {
  episodeId: string;
  /** The owner's id when the episode was shared with the user. */
  ownerId?: string | null;
  /** Viewers see the named people only, without naming tools. */
  readOnly?: boolean;
  /** Called after any change, with how many people are named in the episode. */
  onNamedCount?: (n: number) => void;
}

/**
 * "Who's in these photos?": faces found on this device, grouped by person.
 * Unnamed groups ask for a name and relation; automatic matches ask for a
 * yes/no. Only confirmed names reach the AI story.
 */
export default function PeoplePanel({ episodeId, ownerId = null, readOnly = false, onNamedCount }: Props) {
  const [data, setData] = useState<EpisodePeopleResponse | null>(null);
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([api.episodePeople(episodeId), api.people(ownerId)]);
      setData(d);
      setPeople(p.people);
      setError(null);
      onNamedCount?.(d.groups.filter((g) => g.person).length);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [episodeId, ownerId, onNamedCount]);

  useEffect(() => {
    void load();
  }, [load]);

  // Big episodes are scanned in the background: check again until done.
  useEffect(() => {
    if (!data?.pending) return;
    const t = window.setTimeout(() => void load(), 3000);
    return () => window.clearTimeout(t);
  }, [data, load]);

  if (error) return <p className="text-sm text-red-300/90">{error}</p>;
  if (!data) {
    return (
      <p className="text-sm text-white/50" aria-live="polite" aria-busy="true">
        Looking for faces in your photos…
      </p>
    );
  }
  if (!data.enabled) {
    return (
      <p className="text-sm text-white/50">
        Face finding is not installed on this computer. Run <code>npm run models:download -w server</code> and restart the server.
      </p>
    );
  }

  const named = data.groups.filter((g) => g.person);
  const allUnnamed = readOnly ? [] : data.groups.filter((g) => !g.person);
  const unnamed = showAll ? allUnnamed : allUnnamed.slice(0, FIRST_UNNAMED);
  const more = allUnnamed.length - unnamed.length;

  if (readOnly && named.length === 0) {
    return <p className="text-sm text-white/50">Nobody has been named here yet.</p>;
  }

  return (
    <PanelContext.Provider value={{ owner: ownerId, readOnly }}>
    <div>
      {data.pending > 0 && (
        <p className="mb-3 text-xs text-white/45" aria-live="polite">
          Still looking at {photos(data.pending)}…
        </p>
      )}
      {data.groups.length === 0 && data.pending === 0 && (
        <p className="text-sm text-white/50">No faces were found in these memories.</p>
      )}

      {unnamed.length > 0 && (
        <ul className="space-y-3">
          {unnamed.map((g) => (
            <li key={g.key}>
              <UnnamedGroup group={g} people={people} onSaved={load} />
            </li>
          ))}
        </ul>
      )}
      {more > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="focus-ring mt-3 rounded text-sm text-white/55 underline decoration-white/20 underline-offset-4 hover:text-white"
        >
          Show {more} more {more === 1 ? 'person' : 'people'} to name
        </button>
      )}

      {named.length > 0 && (
        <ul className={`space-y-3 ${unnamed.length > 0 ? 'mt-6' : ''}`}>
          {named.map((g) => (
            <li key={g.key}>
              <NamedGroup group={g} onSaved={load} />
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <p className="mt-4 text-xs text-white/35">
          Faces are found and matched on this computer. The AI story only gets the names you confirm, never faces.
          {data.hiddenCount > 0 && ` ${data.hiddenCount} face${data.hiddenCount === 1 ? '' : 's'} you chose not to name ${data.hiddenCount === 1 ? 'is' : 'are'} hidden.`}
        </p>
      )}
    </div>
    </PanelContext.Provider>
  );
}

const card = 'rounded-xl bg-white/[0.04] p-4 ring-1 ring-white/10';
const inputCls =
  'focus-ring w-full rounded-md bg-white/[0.07] px-3 py-2.5 text-sm text-white ring-1 ring-white/10 placeholder:text-white/30';
/** Native dropdowns follow the dark theme (the popup list included). */
const selectCls = `${inputCls} [color-scheme:dark] appearance-auto`;
const optionCls = 'bg-[#16161c] text-white';
const smallBtn =
  'focus-ring rounded-md px-3 py-2 text-xs font-semibold transition disabled:opacity-40';

function FaceStrip({
  group,
  onRemove,
  removeLabel,
}: {
  group: PeopleGroup;
  onRemove?: (faceId: string) => void;
  removeLabel?: string;
}) {
  const rest = group.faces.slice(1, 9);
  if (rest.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {rest.map((f) => (
        <span key={f.id} className="relative">
          <span className={f.confirmed || !group.person ? '' : 'opacity-60'}>
            <FaceImg faceId={f.id} size="sm" />
          </span>
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(f.id)}
              aria-label={removeLabel}
              title={removeLabel}
              className="focus-ring absolute -top-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-black/80 text-[11px] text-white/80 ring-1 ring-white/20 hover:bg-red-600"
            >
              ×
            </button>
          )}
        </span>
      ))}
      {group.faces.length > 9 && <span className="self-center text-xs text-white/40">+{group.faces.length - 9}</span>}
    </div>
  );
}

function UnnamedGroup({ group, people, onSaved }: { group: PeopleGroup; people: PersonSummary[]; onSaved: () => Promise<void> }) {
  const [name, setName] = useState('');
  const [relation, setRelation] = useState<Relation | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const faceIds = group.faces.map((f) => f.id);
  const { owner } = useContext(PanelContext);

  async function run(body: Parameters<typeof api.assignFaces>[0]) {
    setBusy(true);
    setError(null);
    try {
      await api.assignFaces(body, owner);
      await onSaved();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const typed = name.trim();
    if (!typed) return;
    // Typing a name the user already gave someone means that person.
    const existing = people.find((p) => p.name.toLowerCase() === typed.toLowerCase());
    void run(
      existing
        ? { faceIds, personId: existing.id }
        : { faceIds, newPerson: { name: typed, relation: relation || null } },
    );
  }

  const inputId = `name-${group.key}`;
  return (
    <form onSubmit={submit} className={card} aria-busy={busy}>
      <div className="flex items-center gap-4">
        <FaceImg faceId={group.faces[0].id} />
        <div className="min-w-0">
          <label htmlFor={inputId} className="block text-base font-semibold">
            Who’s this?
          </label>
          <p className="text-xs text-white/45">In {photos(new Set(group.faces.map((f) => f.mediaId)).size)}</p>
        </div>
      </div>
      <FaceStrip group={group} />

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <input
          id={inputId}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name, e.g. Ammi"
          maxLength={60}
          autoComplete="off"
          className={inputCls}
        />
        <select
          value={relation}
          onChange={(e) => setRelation(e.target.value as Relation | '')}
          aria-label="How are they related to you?"
          className={selectCls}
        >
          <option value="" className={optionCls}>Relation (optional)</option>
          {RELATION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className={optionCls}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {people.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-white/40">Or it’s:</span>
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busy}
              onClick={() => void run({ faceIds, personId: p.id })}
              className="focus-ring rounded-full bg-white/[0.07] px-3 py-1.5 text-xs text-white/80 ring-1 ring-white/10 hover:bg-white/[0.14]"
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {error && <p className="mt-3 text-xs text-red-300/90">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button type="submit" disabled={busy || !name.trim()} className={`${smallBtn} bg-white text-black hover:bg-white/85`}>
          Save name
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void run({ faceIds, action: 'hide' })}
          className={`${smallBtn} text-white/55 hover:text-white`}
        >
          Don’t name
        </button>
      </div>
    </form>
  );
}

function NamedGroup({ group, onSaved }: { group: PeopleGroup; onSaved: () => Promise<void> }) {
  const person = group.person!;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(person.name);
  const [relation, setRelation] = useState<Relation | ''>(person.relation ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { owner, readOnly } = useContext(PanelContext);
  const unconfirmed = readOnly ? [] : group.faces.filter((f) => !f.confirmed).map((f) => f.id);
  const count = new Set(group.faces.map((f) => f.mediaId)).size;

  async function run(task: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await task();
      await onSaved();
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const label = relationLabel(person.relation);
  return (
    <div className={card} aria-busy={busy}>
      <div className="flex items-center gap-4">
        <FaceImg faceId={group.faces[0].id} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold">{person.name}</p>
          <p className="text-xs text-white/45">
            {label ? `${label} · ` : ''}in {photos(count)}
          </p>
        </div>
        {!editing && !readOnly && (
          <button type="button" onClick={() => setEditing(true)} className={`${smallBtn} text-white/55 hover:text-white`}>
            Edit
          </button>
        )}
      </div>

      {unconfirmed.length > 0 && (
        <div className="mt-3 rounded-lg bg-amber-300/[0.07] p-3 ring-1 ring-amber-200/15">
          <p className="text-sm text-amber-100/90">
            Is this {person.name}? We matched {unconfirmed.length === group.faces.length ? 'these faces' : `${unconfirmed.length} more`} by ourselves.
          </p>
          <p className="mt-0.5 text-xs text-white/40">The story only uses names you confirm.</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => api.assignFaces({ faceIds: unconfirmed, personId: person.id }, owner))}
              className={`${smallBtn} bg-white text-black hover:bg-white/85`}
            >
              Yes, it’s {person.name}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => api.assignFaces({ faceIds: unconfirmed, action: 'reject' }, owner))}
              className={`${smallBtn} text-white/60 hover:text-white`}
            >
              No
            </button>
          </div>
        </div>
      )}

      <FaceStrip
        group={group}
        removeLabel={`Not ${person.name}`}
        onRemove={readOnly ? undefined : (faceId) => void run(() => api.assignFaces({ faceIds: [faceId], action: 'reject' }, owner))}
      />

      {editing && (
        <form
          className="mt-4 grid gap-2 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void run(() => api.updatePerson(person.id, { name: name.trim(), relation: relation || null }, owner));
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} aria-label="Name" className={inputCls} />
          <select
            value={relation}
            onChange={(e) => setRelation(e.target.value as Relation | '')}
            aria-label="Relation"
            className={selectCls}
          >
            <option value="" className={optionCls}>No relation</option>
            {RELATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className={optionCls}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <button type="submit" disabled={busy || !name.trim()} className={`${smallBtn} bg-white text-black hover:bg-white/85`}>
              Save
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => api.assignFaces({ faceIds: group.faces.map((f) => f.id), action: 'reject' }, owner))}
              className={`${smallBtn} text-white/60 hover:text-white`}
            >
              These aren’t {person.name}
            </button>
            <button type="button" onClick={() => setEditing(false)} className={`${smallBtn} text-white/45 hover:text-white`}>
              Cancel
            </button>
          </div>
          <p className="text-xs text-white/35 sm:col-span-2">A new name applies to {person.name} in every episode.</p>
        </form>
      )}
      {error && <p className="mt-3 text-xs text-red-300/90">{error}</p>}
    </div>
  );
}
